import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill, ProjectMeta } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import api, { fmtZAR, formatApiErrorDetail, PROJECT_TYPE_LABELS } from "@/lib/api";
import {
  Sparkles, FileText, ClipboardList, MessageSquare, Wallet, Layers, RefreshCw,
  ShieldCheck, Calendar, Loader2, Send, ArrowLeft, CheckCircle2, FolderOpen, Download,
  Ruler, MessageCircleQuestion, Lock, StickyNote,
} from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import ProjectFiles from "@/components/ProjectFiles";
import StatusTimeline from "@/components/StatusTimeline";

const TABS = [
  { k: "overview", l: "Overview", icon: Layers },
  { k: "quote", l: "Quote", icon: FileText },
  { k: "survey", l: "Survey", icon: ClipboardList },
  { k: "milestones", l: "Milestones", icon: Layers },
  { k: "payments", l: "Payments", icon: Wallet },
  { k: "files", l: "Files", icon: FolderOpen },
  { k: "messages", l: "Messages", icon: MessageSquare },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [project, setProject] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [payments, setPayments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, q, s, m, pay, msgs] = await Promise.all([
        api.get(`/projects/${id}`).then((r) => r.data),
        api.get(`/quotes?project_id=${id}`).then((r) => r.data).catch(() => []),
        api.get(`/surveys`).then((r) => r.data.filter((x) => x.project_id === id)).catch(() => []),
        api.get(`/projects/${id}/milestones`).then((r) => r.data).catch(() => []),
        api.get(`/projects/${id}/payments`).then((r) => r.data).catch(() => []),
        api.get(`/messages?project_id=${id}`).then((r) => r.data).catch(() => []),
      ]);
      setProject(p);
      setQuotes(q);
      setSurveys(s);
      setMilestones(m);
      setPayments(pay);
      setMessages(msgs);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !project) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-6 py-10 overline">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Link to="/projects" className="text-sm text-[var(--bq-text-muted)] hover:text-[var(--bq-navy)] inline-flex items-center gap-1 mb-4" data-testid="back-to-projects">
          <ArrowLeft size={14} /> Back to projects
        </Link>
        <PageHeader
          overline={`Project · ${project.id.slice(0, 8)}`}
          title={project.title}
          description={project.address}
          actions={<StatusPill status={project.status} />}
        />

        <div className="bq-card p-4 mb-4">
          <StatusTimeline status={project.status} />
        </div>

        {project.status === "rejected" && (
          <div className="bq-card p-4 mb-4 border-l-4" style={{ borderLeftColor: "var(--bq-red)" }} data-testid="rejection-banner">
            <div className="overline" style={{ color: "var(--bq-red)" }}>Request declined</div>
            <p className="text-sm mt-1"><b>{project.rejection_category?.replace(/_/g, " ")}:</b> {project.rejection_reason}</p>
          </div>
        )}

        {project.status === "awaiting_customer_info" && project.info_request && user?.role === "customer" && (
          <InfoResponseBanner project={project} onReload={reload} />
        )}

        <div className="bq-card p-6 mb-6">
          <ProjectMeta project={project} />
          {project.description && (
            <p className="mt-5 text-sm text-[var(--bq-text-muted)] border-t border-[var(--bq-border)] pt-4">{project.description}</p>
          )}
        </div>

        {user?.role !== "customer" && <InternalNotes projectId={project.id} />}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[var(--bq-border)] overflow-x-auto" data-testid="project-tabs">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all flex items-center gap-2 ${
                tab === t.k ? "border-[var(--bq-orange)] text-[var(--bq-navy)]" : "border-transparent text-[var(--bq-text-muted)] hover:text-[var(--bq-navy)]"
              }`}>
              <t.icon size={14} /> {t.l}
            </button>
          ))}
        </div>

        <div className="py-6">
          {tab === "overview" && <Overview project={project} user={user} onReload={reload} />}
          {tab === "quote" && <QuoteTab project={project} quotes={quotes} user={user} onReload={reload} />}
          {tab === "survey" && <SurveyTab project={project} surveys={surveys} user={user} onReload={reload} />}
          {tab === "milestones" && <MilestonesTab project={project} milestones={milestones} user={user} onReload={reload} />}
          {tab === "payments" && <PaymentsTab payments={payments} user={user} onReload={reload} />}
          {tab === "files" && <ProjectFiles projectId={project.id} />}
          {tab === "messages" && <MessagesTab projectId={project.id} messages={messages} onReload={reload} />}
        </div>
      </div>
    </Layout>
  );
}

// ===================== OVERVIEW =====================
function Overview({ project, user, onReload }) {
  const [busy, setBusy] = useState(false);
  const e = project.estimate;
  const requestQuote = async () => {
    setBusy(true);
    try { await api.post(`/projects/${project.id}/request-detailed-quote`); await onReload(); }
    finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try { await api.post(`/projects/${project.id}/regenerate-estimate`); await onReload(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bq-card p-6" data-testid="estimate-card">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <div className="overline flex items-center gap-2">
              <Sparkles size={12} className="text-[var(--bq-orange)]" /> AI Rough Estimate
            </div>
            <h3 className="font-display text-2xl font-bold mt-1">
              {e ? <>{fmtZAR(e.min)} – {fmtZAR(e.max)} <span className="text-base text-[var(--bq-text-muted)] font-normal">{e.currency || "ZAR"}</span></> : "No estimate yet"}
            </h3>
          </div>
          <button onClick={regen} disabled={busy} className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-2 border border-[var(--bq-border)] rounded-md hover:border-[var(--bq-navy)] disabled:opacity-50" data-testid="regen-estimate">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regenerate
          </button>
        </div>
        {e && (
          <>
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <span className="inline-flex items-center gap-1 text-[var(--bq-green)] font-semibold">
                <ShieldCheck size={14} /> {e.confidence}% confidence
              </span>
              <span className="text-[var(--bq-text-muted)]">~ {e.duration_weeks} weeks duration</span>
              {e.metadata?.plan_informed && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full text-white"
                  style={{ background: "var(--bq-navy)" }}
                  title={
                    `Estimate informed by AI plan takeoff: ` +
                    `${e.metadata.takeoff_floor_area_m2 ? e.metadata.takeoff_floor_area_m2 + ' m² · ' : ''}` +
                    `${e.metadata.takeoff_room_count || 0} rooms detected`
                  }
                  data-testid="estimate-plan-informed-badge"
                >
                  <Ruler size={12} /> Plan-informed
                </span>
              )}
            </div>
            {e.summary && <p className="text-sm text-[var(--bq-text-muted)] mt-3">{e.summary}</p>}
            {e.metadata?.flags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="estimate-flags">
                {e.metadata.flags.slice(0, 8).map((f) => (
                  <span key={f} className="pill pill-quote text-xs">{f}</span>
                ))}
              </div>
            )}
            <div className="mt-5 grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="overline mb-1.5">Includes</div>
                <ul className="space-y-1">{(e.includes || []).map((x, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 size={12} className="text-[var(--bq-green)] mt-1 shrink-0" /> {x}</li>)}</ul>
              </div>
              <div>
                <div className="overline mb-1.5">Excludes</div>
                <ul className="space-y-1 text-[var(--bq-text-muted)]">{(e.excludes || []).map((x, i) => <li key={i}>· {x}</li>)}</ul>
              </div>
            </div>
          </>
        )}
        {user.role === "customer" && ["estimate_ready", "submitted"].includes(project.status) && (
          <button onClick={requestQuote} disabled={busy} data-testid="request-quote-btn"
            className="mt-6 bq-orange-btn px-5 py-2.5 rounded-md font-semibold">
            Request detailed quote
          </button>
        )}
      </div>
      {project.completion_pct > 0 && (
        <div className="bq-card p-6" data-testid="progress-card">
          <div className="overline mb-2">Progress</div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-3xl font-bold">{project.completion_pct}%</div>
            <StatusPill status={project.status} />
          </div>
          <div className="h-2 bg-[var(--bq-bg)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--bq-green)]" style={{ width: `${project.completion_pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== QUOTE =====================
function QuoteTab({ project, quotes, user, onReload }) {
  return (
    <div className="space-y-5">
      {user.role === "estimator" || user.role === "admin" ? (
        <QuoteBuilder project={project} onCreated={onReload} />
      ) : null}
      {quotes.length === 0 ? (
        <div className="bq-card p-10 text-center" data-testid="no-quotes">
          <FileText size={28} className="mx-auto text-[var(--bq-text-muted)]" />
          <div className="overline mt-3">No quotes yet</div>
          <p className="text-sm text-[var(--bq-text-muted)] mt-2">
            {project.status === "quote_requested"
              ? "Your detailed quote is being prepared — usually 24–48h."
              : "Request a detailed quote from the Overview tab."}
          </p>
        </div>
      ) : (
        quotes.map((q) => <QuoteCard key={q.id} quote={q} user={user} onReload={onReload} />)
      )}
    </div>
  );
}

function QuoteBuilder({ project, onCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  const suggest = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post(`/ai/suggest-quote-items/${project.id}`);
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const updateItem = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: key === "description" || key === "category" || key === "unit" ? val : parseFloat(val) || 0 } : it));
  };
  const addItem = () => setItems((arr) => [...arr, { category: "Finishes", description: "", quantity: 1, unit: "lot", unit_price: 0 }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, k) => k !== i));

  const subtotal = items.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
  const vat = subtotal * 0.15;
  const total = subtotal + vat;

  const send = async () => {
    setLoading(true); setError("");
    try {
      await api.post("/quotes", { project_id: project.id, items, notes, valid_days: 30 });
      onCreated();
      setItems([]); setNotes("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="bq-card p-6" data-testid="quote-builder">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Quote builder</div>
          <h3 className="font-display text-xl font-bold mt-1">Itemise this project</h3>
        </div>
        <button onClick={suggest} disabled={loading} className="bq-navy-btn px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="ai-suggest-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} AI suggest items
        </button>
      </div>

      {items.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-[var(--bq-text-muted)]">
            <tr className="border-b border-[var(--bq-border)]">
              <th className="text-left py-2 overline">Category</th>
              <th className="text-left py-2 overline">Description</th>
              <th className="text-right py-2 overline">Qty</th>
              <th className="text-left py-2 overline">Unit</th>
              <th className="text-right py-2 overline">Rate</th>
              <th className="text-right py-2 overline">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b border-[var(--bq-border)]" data-testid={`item-row-${i}`}>
                <td className="py-2 pr-2"><input value={it.category} onChange={(e) => updateItem(i, "category", e.target.value)} className="w-32 border border-[var(--bq-border)] rounded-md px-2 py-1" /></td>
                <td className="py-2 pr-2"><input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className="w-full border border-[var(--bq-border)] rounded-md px-2 py-1" /></td>
                <td className="py-2 pr-2 text-right"><input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="w-16 text-right border border-[var(--bq-border)] rounded-md px-2 py-1" /></td>
                <td className="py-2 pr-2"><input value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} className="w-16 border border-[var(--bq-border)] rounded-md px-2 py-1" /></td>
                <td className="py-2 pr-2 text-right"><input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="w-24 text-right border border-[var(--bq-border)] rounded-md px-2 py-1" /></td>
                <td className="py-2 pr-2 text-right font-medium">{fmtZAR(it.quantity * it.unit_price)}</td>
                <td className="py-2"><button onClick={() => removeItem(i)} className="text-[var(--bq-text-muted)] hover:text-[var(--bq-red)] text-xs" data-testid={`remove-item-${i}`}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex justify-between items-center mt-4">
        <button onClick={addItem} className="text-sm text-[var(--bq-orange)] font-semibold" data-testid="add-item-btn">+ Add line item</button>
        {items.length > 0 && (
          <div className="text-sm space-y-0.5 text-right">
            <div>Subtotal: <span className="font-semibold">{fmtZAR(subtotal)}</span></div>
            <div className="text-[var(--bq-text-muted)]">VAT 15%: {fmtZAR(vat)}</div>
            <div className="font-display text-xl font-extrabold">{fmtZAR(total)}</div>
          </div>
        )}
      </div>

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        placeholder="Notes, exclusions, assumptions…" data-testid="quote-notes"
        className="mt-4 w-full px-3 py-2 border border-[var(--bq-border)] rounded-md text-sm" />

      {error && <div className="mt-3 text-sm text-[var(--bq-red)]" data-testid="quote-error">{error}</div>}

      <button onClick={send} disabled={items.length === 0 || loading} data-testid="send-quote-btn"
        className="mt-4 bq-orange-btn px-5 py-2.5 rounded-md font-semibold disabled:opacity-50">
        {loading ? "Sending…" : "Send quote to customer"}
      </button>
    </div>
  );
}

function QuoteCard({ quote, user, onReload }) {
  const [showApprove, setShowApprove] = useState(false);
  const [signature, setSignature] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const approve = async () => {
    if (!signature || !otp) { setErr("Signature and OTP required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/quotes/approve", { quote_id: quote.id, signature, otp });
      setShowApprove(false);
      await onReload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bq-card p-6" data-testid={`quote-card-${quote.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Quote · {quote.id.slice(0, 8)}</div>
          <h3 className="font-display text-2xl font-bold mt-1">{fmtZAR(quote.total)}</h3>
          <div className="text-xs text-[var(--bq-text-muted)] mt-0.5">by {quote.estimator_name || "—"}</div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/quotes/${quote.id}/pdf`}
            target="_blank" rel="noreferrer"
            data-testid={`download-pdf-${quote.id}`}
            className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--bq-border)] rounded-md hover:border-[var(--bq-navy)] transition-all"
          >
            <Download size={12} /> PDF
          </a>
          <StatusPill status={quote.status === "sent" ? "quote_ready" : quote.status === "approved" ? "approved" : "submitted"} />
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="text-[var(--bq-text-muted)]">
          <tr className="border-b border-[var(--bq-border)]">
            <th className="text-left py-2 overline">Category</th>
            <th className="text-left py-2 overline">Description</th>
            <th className="text-right py-2 overline">Qty</th>
            <th className="text-right py-2 overline">Rate</th>
            <th className="text-right py-2 overline">Total</th>
          </tr>
        </thead>
        <tbody>
          {(quote.items || []).map((it, i) => (
            <tr key={i} className="border-b border-[var(--bq-border)]">
              <td className="py-2 font-medium">{it.category}</td>
              <td className="py-2">{it.description}</td>
              <td className="py-2 text-right">{it.quantity} {it.unit}</td>
              <td className="py-2 text-right">{fmtZAR(it.unit_price)}</td>
              <td className="py-2 text-right font-semibold">{fmtZAR(it.quantity * it.unit_price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 text-right text-sm space-y-0.5">
        <div>Subtotal: <span className="font-semibold">{fmtZAR(quote.subtotal)}</span></div>
        <div className="text-[var(--bq-text-muted)]">VAT 15%: {fmtZAR(quote.vat)}</div>
        <div className="font-display text-2xl font-extrabold">{fmtZAR(quote.total)}</div>
      </div>

      {quote.notes && (
        <div className="mt-4 text-sm bg-[var(--bq-bg)] rounded-md p-3 text-[var(--bq-text-muted)]">{quote.notes}</div>
      )}

      {user.role === "customer" && quote.status === "sent" && !showApprove && (
        <button onClick={() => setShowApprove(true)} data-testid={`approve-quote-${quote.id}`}
          className="mt-5 bq-orange-btn px-5 py-2.5 rounded-md font-semibold">
          Review &amp; approve
        </button>
      )}

      {showApprove && (
        <div className="mt-5 border-t border-[var(--bq-border)] pt-5" data-testid="approval-panel">
          <div className="overline mb-3">Digital approval</div>
          <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full name as signature"
            data-testid="signature-input"
            className="w-full px-3 py-2.5 border border-[var(--bq-border)] rounded-md mb-3 font-display italic" />
          <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="OTP (any 4 digits for demo)"
            data-testid="otp-input"
            className="w-full px-3 py-2.5 border border-[var(--bq-border)] rounded-md mb-3 tracking-widest" />
          {err && <div className="text-sm text-[var(--bq-red)] mb-3">{err}</div>}
          <div className="flex gap-2">
            <button onClick={approve} disabled={busy} data-testid="confirm-approve-btn"
              className="bq-orange-btn px-5 py-2.5 rounded-md font-semibold disabled:opacity-50">
              {busy ? "Approving…" : "Approve quote"}
            </button>
            <button onClick={() => setShowApprove(false)} className="px-5 py-2.5 rounded-md border border-[var(--bq-border)] text-sm font-semibold">
              Cancel
            </button>
          </div>
        </div>
      )}

      {quote.status === "approved" && (
        <div className="mt-5 p-4 rounded-md bg-emerald-50 border border-emerald-100 flex items-start gap-2" data-testid="approved-banner">
          <CheckCircle2 size={16} className="text-[var(--bq-green)] mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-700">Quote approved</div>
            <div className="text-emerald-700/80">Signed by {quote.approval_signature} · Payment milestones generated.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== SURVEY =====================
function SurveyTab({ project, surveys, user, onReload }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const book = async () => {
    setBusy(true);
    try {
      await api.post("/surveys/book", { project_id: project.id, requested_date: date, notes });
      await onReload();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {user.role === "customer" && surveys.length === 0 && (
        <div className="bq-card p-6" data-testid="book-survey-card">
          <div className="overline">Book a site survey</div>
          <h3 className="font-display text-xl font-bold mt-1">Pick a date</h3>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="overline">Preferred date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="survey-date"
                className="mt-1 w-full px-3 py-2.5 border border-[var(--bq-border)] rounded-md" />
            </div>
            <div>
              <label className="overline">Access notes</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)}
                data-testid="survey-notes"
                placeholder="Gate code, parking…"
                className="mt-1 w-full px-3 py-2.5 border border-[var(--bq-border)] rounded-md" />
            </div>
          </div>
          <button onClick={book} disabled={busy} data-testid="book-survey-btn"
            className="mt-4 bq-orange-btn px-5 py-2.5 rounded-md font-semibold disabled:opacity-50">
            <Calendar size={14} className="inline mr-1" /> Book survey
          </button>
        </div>
      )}
      {surveys.map((s) => <SurveyCard key={s.id} survey={s} user={user} onReload={onReload} />)}
      {surveys.length === 0 && user.role !== "customer" && (
        <div className="bq-card p-10 text-center"><div className="overline">No survey requested yet</div></div>
      )}
    </div>
  );
}

function SurveyCard({ survey, user, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ measurements: "", risks: "", access_constraints: "", recommendations: "", photo_file_ids: [] });
  const [busy, setBusy] = useState(false);

  const assignSelf = async () => {
    setBusy(true);
    try { await api.post(`/surveys/${survey.id}/assign`, {}); await onReload(); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/surveys/${survey.id}/report`, form); await onReload(); }
    finally { setBusy(false); }
  };

  return (
    <div className="bq-card p-6" data-testid={`survey-card-${survey.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="overline">Survey · {survey.id.slice(0, 8)}</div>
          <h3 className="font-display text-xl font-bold mt-1">
            {new Date(survey.requested_date).toLocaleDateString("en-ZA", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
          </h3>
          <div className="text-sm text-[var(--bq-text-muted)] mt-1">
            {survey.surveyor_name ? `Surveyor: ${survey.surveyor_name}` : "Awaiting surveyor assignment"}
          </div>
        </div>
        <span className={`pill ${survey.status === "completed" ? "pill-approved" : survey.status === "scheduled" ? "pill-progress" : "pill-quote"}`}>
          {survey.status}
        </span>
      </div>
      {survey.notes && <div className="text-sm text-[var(--bq-text-muted)] mb-3">{survey.notes}</div>}

      {user.role === "surveyor" && survey.status === "requested" && (
        <button onClick={assignSelf} disabled={busy} data-testid="assign-self-btn"
          className="bq-navy-btn px-4 py-2 rounded-md text-sm font-semibold">Assign to me</button>
      )}

      {user.role === "surveyor" && survey.status === "scheduled" && !open && (
        <button onClick={() => setOpen(true)} data-testid="open-report-form" className="bq-orange-btn px-4 py-2 rounded-md text-sm font-semibold">Submit report</button>
      )}

      {open && (
        <div className="space-y-3 mt-4" data-testid="survey-report-form">
          <textarea rows={2} placeholder="Measurements (e.g. 4.2m × 5.8m kitchen)"
            value={form.measurements} onChange={(e) => setForm({ ...form, measurements: e.target.value })}
            data-testid="report-measurements"
            className="w-full px-3 py-2 border border-[var(--bq-border)] rounded-md text-sm" />
          <textarea rows={2} placeholder="Site risks"
            value={form.risks} onChange={(e) => setForm({ ...form, risks: e.target.value })}
            data-testid="report-risks"
            className="w-full px-3 py-2 border border-[var(--bq-border)] rounded-md text-sm" />
          <textarea rows={2} placeholder="Access constraints"
            value={form.access_constraints} onChange={(e) => setForm({ ...form, access_constraints: e.target.value })}
            data-testid="report-access"
            className="w-full px-3 py-2 border border-[var(--bq-border)] rounded-md text-sm" />
          <textarea rows={2} placeholder="Recommendations"
            value={form.recommendations} onChange={(e) => setForm({ ...form, recommendations: e.target.value })}
            data-testid="report-recommendations"
            className="w-full px-3 py-2 border border-[var(--bq-border)] rounded-md text-sm" />
          <button onClick={submit} disabled={busy} data-testid="submit-report-btn"
            className="bq-orange-btn px-5 py-2.5 rounded-md font-semibold disabled:opacity-50">Submit report</button>
        </div>
      )}

      {survey.report && (
        <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
          <Detail label="Measurements" value={survey.report.measurements} />
          <Detail label="Risks" value={survey.report.risks} />
          <Detail label="Access constraints" value={survey.report.access_constraints} />
          <Detail label="Recommendations" value={survey.report.recommendations} />
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1 whitespace-pre-line">{value || "—"}</div>
    </div>
  );
}

// ===================== MILESTONES =====================
function MilestonesTab({ milestones, user, onReload }) {
  if (milestones.length === 0) {
    return <div className="bq-card p-10 text-center"><div className="overline">No milestones yet</div><p className="text-sm text-[var(--bq-text-muted)] mt-2">Milestones are auto-generated after quote approval.</p></div>;
  }
  return (
    <div className="bq-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bq-bg)]">
          <tr>
            <th className="text-left px-6 py-3 overline">Phase</th>
            <th className="text-left px-6 py-3 overline">Status</th>
            <th className="text-left px-6 py-3 overline">Progress</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {milestones.map((m) => <MilestoneRow key={m.id} m={m} user={user} onReload={onReload} />)}
        </tbody>
      </table>
    </div>
  );
}
function MilestoneRow({ m, user, onReload }) {
  const [pct, setPct] = useState(m.progress_pct);
  const editable = user.role === "project_manager" || user.role === "admin";
  const save = async () => {
    const status = pct === 0 ? "pending" : pct >= 100 ? "done" : "in_progress";
    await api.patch(`/milestones/${m.id}`, { progress_pct: pct, status });
    await onReload();
  };
  return (
    <tr className="border-t border-[var(--bq-border)]" data-testid={`milestone-${m.id}`}>
      <td className="px-6 py-4 font-semibold">{m.title}</td>
      <td className="px-6 py-4"><span className={`pill ${m.status === "done" ? "pill-approved" : m.status === "in_progress" ? "pill-progress" : "pill-default"}`}>{m.status}</span></td>
      <td className="px-6 py-4 w-1/3">
        <div className="flex items-center gap-3">
          <input type="range" min={0} max={100} value={pct} disabled={!editable}
            onChange={(e) => setPct(parseInt(e.target.value))}
            data-testid={`milestone-slider-${m.id}`}
            className="flex-1 accent-[var(--bq-orange)]" />
          <span className="font-mono text-xs w-10 text-right">{pct}%</span>
        </div>
      </td>
      <td className="px-6 py-4 text-right">
        {editable && pct !== m.progress_pct && (
          <button onClick={save} className="text-xs font-semibold text-[var(--bq-orange)]" data-testid={`save-milestone-${m.id}`}>Save</button>
        )}
      </td>
    </tr>
  );
}

// ===================== PAYMENTS =====================
function PaymentsTab({ payments, user, onReload }) {
  if (payments.length === 0) {
    return <div className="bq-card p-10 text-center"><div className="overline">No payment schedule yet</div><p className="text-sm text-[var(--bq-text-muted)] mt-2">Auto-generated after quote approval (30 / 25 / 25 / 20).</p></div>;
  }
  const pay = async (id) => { await api.post(`/payments/${id}/pay`); await onReload(); };
  return (
    <div className="bq-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bq-bg)]">
          <tr>
            <th className="text-left px-6 py-3 overline">Milestone</th>
            <th className="text-left px-6 py-3 overline">%</th>
            <th className="text-right px-6 py-3 overline">Amount</th>
            <th className="text-left px-6 py-3 overline">Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id} className="border-t border-[var(--bq-border)]" data-testid={`payment-${p.id}`}>
              <td className="px-6 py-4 font-semibold">{p.label}</td>
              <td className="px-6 py-4">{p.pct}%</td>
              <td className="px-6 py-4 text-right font-mono">{fmtZAR(p.amount)}</td>
              <td className="px-6 py-4"><span className={`pill ${p.status === "paid" ? "pill-approved" : "pill-quote"}`}>{p.status}</span></td>
              <td className="px-6 py-4 text-right">
                {user.role === "customer" && p.status === "due" && (
                  <button onClick={() => pay(p.id)} className="bq-orange-btn px-4 py-1.5 rounded-md text-xs font-semibold" data-testid={`pay-${p.id}`}>Pay now</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-6 py-3 text-xs bg-[var(--bq-bg)] border-t border-[var(--bq-border)] text-[var(--bq-text-muted)]">
        MOCKED payment flow — clicking &quot;Pay now&quot; marks the milestone paid (no real gateway).
      </div>
    </div>
  );
}

// ===================== MESSAGES =====================
function MessagesTab({ projectId, messages, onReload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post("/messages", { project_id: projectId, body: text }); setText(""); await onReload(); }
    finally { setBusy(false); }
  };
  return (
    <div className="bq-card p-0 flex flex-col" style={{ minHeight: 400 }}>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3" data-testid="messages-list">
        {messages.length === 0 ? (
          <div className="text-center overline py-10">No messages yet</div>
        ) : messages.map((m) => (
          <div key={m.id} className="flex flex-col" data-testid={`message-${m.id}`}>
            <div className="text-xs text-[var(--bq-text-muted)] mb-1">
              <span className="font-semibold text-[var(--bq-navy)]">{m.sender_name}</span> · {m.sender_role} · {new Date(m.created_at).toLocaleString("en-ZA")}
            </div>
            <div className="bg-[var(--bq-bg)] rounded-md px-3 py-2 text-sm">{m.body}</div>
          </div>
        ))}
      </div>
      <div className="border-t border-[var(--bq-border)] p-4 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Write a message…" data-testid="message-input"
          className="flex-1 px-3 py-2.5 border border-[var(--bq-border)] rounded-md text-sm" />
        <button onClick={send} disabled={busy || !text.trim()} data-testid="send-message-btn"
          className="bq-orange-btn px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 disabled:opacity-50">
          <Send size={14} /> Send
        </button>
      </div>
    </div>
  );
}



// ===================== INFO RESPONSE BANNER (customer-facing) =====================
function InfoResponseBanner({ project, onReload }) {
  const req = project.info_request || {};
  const [note, setNote] = useState("");
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const done = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
    const body = note.trim()
      || (done.length ? `Provided: ${done.join("; ")}` : "");
    if (!body) { toast.error("Add a note or tick at least one item."); return; }
    setSubmitting(true);
    try {
      await api.post(`/projects/${project.id}/respond-info`, { note: body });
      toast.success("Sent — admin will review again.");
      await onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bq-card p-5 mb-4 border-l-4" style={{ borderLeftColor: "var(--bq-amber)" }} data-testid="info-request-banner">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircleQuestion size={18} className="text-[var(--bq-amber)]" />
        <h3 className="font-display font-bold">Our team needs a bit more info</h3>
      </div>
      {req.note && <p className="text-sm text-[var(--bq-text-muted)] mb-3">{req.note}</p>}
      {(req.checklist || []).length > 0 && (
        <ul className="space-y-1.5 mb-4">
          {(req.checklist || []).map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={!!checked[it]} onChange={(e) => setChecked({ ...checked, [it]: e.target.checked })} data-testid={`info-check-${i}`} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
      <textarea
        value={note} onChange={(e) => setNote(e.target.value)} rows={3}
        className="bq-input w-full"
        placeholder="Your reply / additional info…"
        data-testid="info-response-note"
      />
      <button
        onClick={submit} disabled={submitting}
        className="mt-3 inline-flex items-center gap-2 bq-orange-btn px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-50"
        data-testid="info-response-submit"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to admin
      </button>
    </div>
  );
}

// ===================== INTERNAL NOTES (staff-only) =====================
function InternalNotes({ projectId }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/notes`);
      setNotes(data || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/notes`, { body: body.trim() });
      setBody("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="bq-card p-5 mb-6" data-testid="internal-notes">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between"
        data-testid="internal-notes-toggle"
      >
        <div className="flex items-center gap-2">
          <Lock size={16} className="text-[var(--bq-text-muted)]" />
          <span className="font-display font-bold">Internal notes</span>
          <span className="overline">Staff only · not visible to customer</span>
          {notes.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bq-bg)] border border-[var(--bq-border)]">{notes.length}</span>
          )}
        </div>
        <span className="text-xs text-[var(--bq-text-muted)]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex gap-2">
            <textarea
              value={body} onChange={(e) => setBody(e.target.value)} rows={2}
              className="bq-input flex-1" placeholder="Triage note for the team…"
              data-testid="internal-note-input"
            />
            <button
              onClick={add} disabled={busy || !body.trim()}
              className="px-3 py-2 bq-orange-btn rounded-md text-sm font-semibold inline-flex items-center gap-1 self-start disabled:opacity-50"
              data-testid="internal-note-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <StickyNote size={14} />} Add
            </button>
          </div>
          {notes.length === 0 ? (
            <div className="text-xs text-[var(--bq-text-muted)] italic">No internal notes yet.</div>
          ) : (
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.id} className="text-sm border-l-2 border-[var(--bq-border)] pl-3" data-testid={`internal-note-${n.id}`}>
                  <div className="flex items-center justify-between gap-2 text-xs text-[var(--bq-text-muted)]">
                    <span><b className="text-[var(--bq-navy)]">{n.author_name}</b> · {n.author_role}</span>
                    <span>{new Date(n.created_at).toLocaleString("en-ZA")}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap">{n.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
